import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, X, AlertCircle, Loader2, Shuffle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useT } from '@/i18n';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';
import { toast } from '@/stores/toast.store';
import { apiCall, ApiError } from '@/lib/api-client';

/**
 * v0.6.0 Cluster L — Admin Bulk Reassign Tool modal.
 *
 * Surface points:
 *   - /admin/uploaders-activity header → audience='uploader'
 *   - /admin (LiveMonitoring) agent section → audience='agent'
 *
 * Form fields:
 *   - audience selector (fixed by caller; rendered as read-only badge)
 *   - source user picker (active users matching audience role)
 *   - target user picker (same; must differ from source)
 *   - count input (positive int, max 1000)
 *   - reason textarea (>=5 chars)
 *
 * Submit calls POST /admin/reassign/bulk. Success → toast with actualCount
 * vs requestedCount (so admin sees when count > available — partial move
 * surfaced clearly). invalidateQueriesKey param lets caller refresh the
 * relevant table after the move.
 */

interface AdminUserOption {
  id: string;
  fullName: string;
  email: string;
  role: string;
  active: boolean;
}

interface UsersListResponse {
  items: AdminUserOption[];
  total: number;
}

export interface BulkReassignModalProps {
  open: boolean;
  /** Fixed audience pre-fills + locks the selector (contextual shortcut from
   *  uploader-specific page); 'choose' renders an in-modal audience selector
   *  (canonical hub at /admin/reassign where admin picks intent). */
  audience: 'agent' | 'uploader' | 'choose';
  onClose: () => void;
  /** TanStack queryKey to invalidate after successful reassign. Caller uses
   *  this to refresh whatever table is showing the affected users. */
  invalidateQueryKey?: readonly unknown[];
}

export function BulkReassignModal(props: BulkReassignModalProps): JSX.Element {
  useBodyScrollLock(props.open);
  return createPortal(
    <AnimatePresence>
      {props.open ? <Inner {...props} /> : null}
    </AnimatePresence>,
    document.body,
  );
}

function Inner({ audience: audienceProp, onClose, invalidateQueryKey }: BulkReassignModalProps): JSX.Element {
  const t = useT();
  const qc = useQueryClient();
  // v0.6.0 Fase 11.9 — when caller passes 'choose', render an in-modal
  // audience selector (canonical /admin/reassign use). When caller passes
  // a concrete audience, lock it (contextual shortcut from uploader page).
  const [audienceState, setAudienceState] = useState<'agent' | 'uploader'>(
    audienceProp === 'choose' ? 'agent' : audienceProp,
  );
  const audience: 'agent' | 'uploader' = audienceProp === 'choose' ? audienceState : audienceProp;
  const isChooseMode = audienceProp === 'choose';
  const [sourceId, setSourceId] = useState('');
  const [targetId, setTargetId] = useState('');
  const [count, setCount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: usersData, isLoading: usersLoading } = useQuery<UsersListResponse>({
    queryKey: ['admin', 'users', 'list', audience],
    queryFn: () => apiCall<UsersListResponse>(`/admin/users?role=${audience}&active=true&limit=200`),
    staleTime: 60_000,
  });

  const eligible = useMemo(
    () => (usersData?.items ?? []).filter((u) => u.role === audience && u.active),
    [usersData, audience],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const mut = useMutation({
    mutationFn: (body: {
      audience: 'agent' | 'uploader';
      sourceUserId: number;
      targetUserId: number;
      count: number;
      reason: string;
    }) =>
      apiCall<{ actualCount: number; requestedCount: number; sourceUserId: number; targetUserId: number }>(
        '/admin/reassign/bulk',
        { method: 'POST', body },
      ),
    onSuccess: (res) => {
      if (invalidateQueryKey) {
        void qc.invalidateQueries({ queryKey: invalidateQueryKey });
      }
      if (res.actualCount < res.requestedCount) {
        toast.success(
          t('bulkReassign.toast.partial'),
          t('bulkReassign.toast.partialDesc', { actual: res.actualCount, requested: res.requestedCount }),
        );
      } else {
        toast.success(
          t('bulkReassign.toast.success'),
          t('bulkReassign.toast.successDesc', { count: res.actualCount }),
        );
      }
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : (err as Error).message || 'Failed');
    },
  });

  const sourceN = Number(sourceId);
  const targetN = Number(targetId);
  const countN = Number(count);
  const canSubmit =
    !mut.isPending &&
    Number.isInteger(sourceN) && sourceN > 0 &&
    Number.isInteger(targetN) && targetN > 0 &&
    sourceN !== targetN &&
    Number.isInteger(countN) && countN > 0 && countN <= 1000 &&
    reason.trim().length >= 5;

  const handleSubmit = (): void => {
    setError(null);
    if (!canSubmit) return;
    mut.mutate({ audience, sourceUserId: sourceN, targetUserId: targetN, count: countN, reason: reason.trim() });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8">
      <motion.div
        className="absolute inset-0 bg-nav-navy/50 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        className="relative bg-white rounded-xl shadow-elevated w-full max-w-lg max-h-[85vh] overflow-y-auto font-body text-nav-navy"
        initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }}
      >
        <header className="sticky top-0 bg-white border-b border-nav-card px-5 py-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Shuffle className="w-5 h-5 text-nav-tealDark" />
            <div>
              <h2 className="font-head text-[15px] font-bold text-nav-navy">{t('bulkReassign.title')}</h2>
              <p className="text-xs text-nav-slate mt-0.5">
                {isChooseMode
                  ? t('bulkReassign.audience.choose')
                  : audience === 'agent'
                    ? t('bulkReassign.audience.agent')
                    : t('bulkReassign.audience.uploader')}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label={t('common.close')} className="text-nav-slate hover:text-nav-navy p-1 rounded">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <p className="text-sm text-nav-navy">{t('bulkReassign.body')}</p>

          {isChooseMode && (
            <div>
              <label className="block text-sm font-medium text-nav-navy mb-1">{t('bulkReassign.audienceLabel')}</label>
              <div className="inline-flex rounded-md border border-nav-card overflow-hidden">
                {(['agent', 'uploader'] as const).map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => {
                      setAudienceState(a);
                      setSourceId('');
                      setTargetId('');
                    }}
                    className={
                      'px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal ' +
                      (audience === a ? 'bg-nav-tealTint text-nav-tealDark' : 'text-nav-navy hover:bg-nav-soft')
                    }
                    aria-pressed={audience === a}
                  >
                    {a === 'agent' ? t('bulkReassign.audienceAgent') : t('bulkReassign.audienceUploader')}
                  </button>
                ))}
              </div>
            </div>
          )}

          {usersLoading ? (
            <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin text-nav-faint mx-auto" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-7 gap-3 items-end">
              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-nav-navy mb-1">{t('bulkReassign.source')}</label>
                <select
                  value={sourceId}
                  onChange={(e) => setSourceId(e.target.value)}
                  className="w-full text-sm rounded-md border border-nav-line px-2 py-2 bg-white focus:ring-2 focus:ring-nav-teal focus:outline-none"
                >
                  <option value="">{t('bulkReassign.pickUser')}</option>
                  {eligible.map((u) => (
                    <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>
                  ))}
                </select>
              </div>
              <div className="hidden sm:flex sm:col-span-1 items-center justify-center pb-2">
                <ArrowRight className="w-5 h-5 text-nav-faint" />
              </div>
              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-nav-navy mb-1">{t('bulkReassign.target')}</label>
                <select
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  className="w-full text-sm rounded-md border border-nav-line px-2 py-2 bg-white focus:ring-2 focus:ring-nav-teal focus:outline-none"
                >
                  <option value="">{t('bulkReassign.pickUser')}</option>
                  {eligible.filter((u) => u.id !== sourceId).map((u) => (
                    <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-nav-navy mb-1">{t('bulkReassign.count')}</label>
            <Input
              type="number"
              min={1}
              max={1000}
              step={1}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              className="w-32 font-mono tabular-nums"
              placeholder="10"
            />
            <p className="text-[11px] text-nav-slate mt-1">{t('bulkReassign.countHint')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-nav-navy mb-1">{t('bulkReassign.reason')} *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full text-sm rounded-md border border-nav-line px-3 py-2 focus:ring-2 focus:ring-nav-teal focus:outline-none"
              placeholder={t('bulkReassign.reasonPlaceholder')}
            />
            <p className="text-[11px] text-nav-slate mt-1">{t('bulkReassign.reasonHint')}</p>
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md bg-nav-redTint border border-[#e3b5ae] px-3 py-2 text-xs text-nav-red">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
              <span>{error}</span>
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-nav-card px-5 py-3 flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={mut.isPending}>{t('common.cancel')}</Button>
          <Button type="button" variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {mut.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden />}
            {t('bulkReassign.submit')}
          </Button>
        </footer>
      </motion.div>
    </div>
  );
}
